import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isNativePlatform,
} from './storage.js';

/** The API subdomain, baked at build time (e.g. https://hfbapi.brytbytes.com). */
const CONFIGURED_API_BASE = (
  import.meta.env.VITE_API_URL || 'http://localhost:4000'
).replace(/\/+$/, '');

/**
 * Where API calls go.
 *
 * - Native app: always the absolute configured API — the Capacitor webview
 *   origin (capacitor://localhost / http://localhost) is not the API's origin,
 *   so a relative path would 404 against the app bundle.
 * - Modern web browsers: the API subdomain directly (cross-origin + CORS). This
 *   is the vast majority of traffic and costs nothing extra.
 * - LEGACY web browsers (old smart-TV engines running the vite-legacy bundle):
 *   old engines can't complete a cross-subdomain fetch, so those — and only
 *   those — go same-origin ("/api" on the site's own domain), where a Cloudflare
 *   Worker proxies to the API. Keeping modern traffic off the Worker avoids
 *   burning its quota — and means a new domain needs no Worker route at all.
 *
 * Deciding which engine this is, without a timing bug:
 *
 * Vite's legacy plugin sets `window.__vite_is_modern_browser`, but it does so
 * from script tags that the plugin injects AFTER the app's own module tag.
 * Module scripts run in document order, so when this file was evaluated at
 * import time the flag was always still undefined — every visitor, on every
 * browser, was quietly sent down the legacy same-origin path and through the
 * Worker. On a domain with no Worker route (a second domain pointed at the
 * same folder) "/api/…" then returns the SPA's own index.html, and the app
 * sees HTML where it expects JSON.
 *
 * So: resolved per request rather than once at import, and `noModule` is the
 * signal. It answers synchronously whenever it's asked, and it is exactly the
 * line the legacy bundle is drawn on — a browser that understands ES modules
 * got the modern bundle and can certainly manage a cross-origin fetch.
 */
function isModernEngine() {
  if (typeof window === 'undefined') return true;
  if (window.__vite_is_modern_browser) return true;
  return typeof HTMLScriptElement !== 'undefined' && 'noModule' in HTMLScriptElement.prototype;
}

function resolveApiBase() {
  if (isNativePlatform()) return CONFIGURED_API_BASE;
  if (typeof window !== 'undefined' && !isModernEngine()) {
    return window.location.origin; // same-origin → Cloudflare Worker proxies /api
  }
  return CONFIGURED_API_BASE;
}

/**
 * Where calls go, asked afresh each time. Exported as a function because the
 * answer depends on the browser, and reading it once at import is the bug
 * described above.
 */
export const apiBaseUrl = () => resolveApiBase();

export class ApiError extends Error {
  constructor(message, { status, data, url } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.url = url;
  }

  /** The sample-box endpoint signals "already claimed" with a 409. */
  get isConflict() {
    return this.status === 409;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }
}

// ---------------------------------------------------------------------------
// Session-expiry notification
// ---------------------------------------------------------------------------

const unauthorizedListeners = new Set();

/**
 * Subscribe to "the session is gone, send the user to /login".
 *
 * We notify rather than calling window.location, because a hard navigation
 * would reload the whole bundle — and under Capacitor's file:// origin that
 * loses the router's in-memory state and can land on a blank page.
 */
export function onUnauthorized(listener) {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized() {
  for (const listener of unauthorizedListeners) {
    try {
      listener();
    } catch {
      /* a bad listener must not break the others */
    }
  }
}

// ---------------------------------------------------------------------------
// Single-flight token refresh
// ---------------------------------------------------------------------------

let refreshPromise = null;

/**
 * Refresh the access token, coalescing concurrent callers onto one request.
 *
 * The API rotates refresh tokens (each successful refresh consumes the old
 * one), so a dashboard firing five parallel requests that all 401 must produce
 * exactly one refresh. Without coalescing, four of the five would present an
 * already-consumed token, fail, and log the user out.
 */
function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new ApiError('No refresh token', { status: 401 });

    const res = await fetch(`${apiBaseUrl()}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      throw new ApiError('Session expired', { status: res.status });
    }

    const data = await res.json();
    await setTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });
    return data.accessToken;
  })();

  // Clear the slot once settled so the *next* genuine expiry can refresh
  // again. Attached with .finally so it runs on both paths.
  refreshPromise = refreshPromise.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

async function parseBody(res) {
  if (res.status === 204) return null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  const text = await res.text();
  return text || null;
}

function errorMessage(body, res) {
  if (body && typeof body === 'object' && body.error) return body.error;
  if (typeof body === 'string' && body) return body;
  return res.statusText || `Request failed with status ${res.status}`;
}

/**
 * Core request. On a 401 it refreshes once and replays the original request.
 */
async function request(method, path, { body, headers, signal, auth = true, responseType, ...rest } = {}) {
  const url = path.startsWith('http')
    ? path
    : `${apiBaseUrl()}${path.startsWith('/') ? path : `/${path}`}`;

  const send = (token) => {
    const finalHeaders = { Accept: 'application/json', ...headers };
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !isFormData) finalHeaders['Content-Type'] = 'application/json';
    if (token) finalHeaders.Authorization = `Bearer ${token}`;

    return fetch(url, {
      method,
      // API answers are live data (an approval, a stock level). Never let the
      // browser or Android WebView HTTP cache hand back an older copy.
      cache: 'no-store',
      headers: finalHeaders,
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      signal,
      ...rest,
    });
  };

  const tokenUsed = auth ? getAccessToken() : null;

  let res;
  try {
    res = await send(tokenUsed);
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new ApiError(
      'Cannot reach the server. Check your connection and try again.',
      { status: 0, url },
    );
  }

  if (res.status === 401 && auth) {
    // If another in-flight request already refreshed while this one was on
    // the wire, our token is simply stale — replay with the current one
    // instead of burning a second (already-rotated) refresh token.
    const current = getAccessToken();
    if (current && current !== tokenUsed) {
      res = await send(current);
    } else {
      try {
        const fresh = await refreshAccessToken();
        res = await send(fresh);
      } catch {
        await clearTokens();
        notifyUnauthorized();
        throw new ApiError('Your session has expired. Please sign in again.', {
          status: 401,
          url,
        });
      }
    }

    // The replay itself came back 401 — the refresh is not the problem.
    if (res.status === 401) {
      await clearTokens();
      notifyUnauthorized();
      throw new ApiError('Your session has expired. Please sign in again.', {
        status: 401,
        url,
      });
    }
  }

  // Files (e.g. identity documents) come back as a Blob, still behind the same
  // auth and refresh handling. Errors fall through and parse as JSON below.
  if (res.ok && responseType === 'blob') return res.blob();

  const data = await parseBody(res);

  if (!res.ok) {
    throw new ApiError(errorMessage(data, res), { status: res.status, data, url });
  }

  return data;
}

export const api = {
  get: (path, options) => request('GET', path, options),
  // An authenticated file download. An <img src> can't send the bearer token,
  // so protected files are fetched as a Blob and shown via an object URL.
  getBlob: (path, options) => request('GET', path, { ...options, responseType: 'blob' }),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  del: (path, options) => request('DELETE', path, options),
};

export default api;
